import { model as i18nModel } from '!/i18n'
import { model as playerModel } from '$/player'
import { model as sessionModel } from '$/session'
import { noop as noopEvent } from '@/effector'
import { nay, noop } from '@/helpers'
import { extendMediaItemByLabelPriceTvChannel } from '@/tools'
import { persist } from '@effector-storage/idb-keyval'
import { api, type TvChannel } from '@setplex/tria-api'
import { combine, createEvent, createStore, sample } from 'effector'
import { produce } from 'immer'
import { debounce } from 'patronum'
import { features } from '~/core/flags'
import { model as session } from '~/entities/session'
import * as pinLockFeature from '~/features/pin-lock'
import { model as guest } from '~/features/subscriber/guest'
import { ONE_MINUTE_MS } from '~/shared/constants/time'
import { ssePurchasesModel } from '../payment/sse-purchases'
import { RECENTLY_WATCHED_RAW_MAX_COUNT } from './index.h'

/*
 * New remote implementation of recently watched channels, available from wbs 3.3
 * (and should be used if there is `appearanceCustomizationEnabled` option EXISTS, with ANY value)
 */

const addChannel = createEvent<number>()
const addChannelDebounced = debounce(addChannel, ONE_MINUTE_MS)

const $candidate = createStore<number | null>(null).reset(sessionModel.$session)
const $justAdded = createStore<number | null>(null).reset(sessionModel.$session)

// fill candidate channel to add to recently watched on play start
sample({
  clock: playerModel.play,
  source: {
    features: features.$featuredFlags,
    isGuest: guest.$guest,
  },
  filter: ({ features, isGuest }, { contentType, contentId }) =>
    features.appearanceCustomizationEnabled != null && // indicates that new implementation can be used
    contentType === 'tv' &&
    contentId != null &&
    contentId !== '' &&
    !isNaN(Number(contentId)) &&
    !isGuest,
  fn: (_, { contentId }) => Number(contentId),
  target: $candidate,
})

// erase candidate channel on play stop
// there is a "problem" with pause - it is not distinguishable from stop in current implementation,
// so we can't skip pause event here, but this is ok now,
// see https://setplexapps.atlassian.net/browse/FP-2678 point 3
sample({
  clock: playerModel.stop,
  fn: () => null,
  target: $candidate,
})

// setup delayed adding of candidate channel to recently watched
sample({
  clock: $candidate,
  filter: (id) => id != null,
  target: addChannel,
})

// actually add channel to recently watched after 1 minute of watching
sample({
  clock: addChannelDebounced,
  source: {
    features: features.$featuredFlags,
    candidateId: $candidate,
    justAdded: $justAdded,
    isGuest: guest.$guest,
  },
  filter: ({ features, candidateId, justAdded, isGuest }, addingId) =>
    features.appearanceCustomizationEnabled != null && // indicates that new implementation can be used
    addingId === candidateId &&
    addingId !== justAdded &&
    !isGuest,
  fn: (_, addingId) => ({ id: addingId }),
  target: api.tv.addRecentlyWatchedChannelFx,
})

// save just added to recently watched channel id, to avoid adding it again
sample({
  clock: api.tv.addRecentlyWatchedChannelFx,
  source: $candidate,
  target: $justAdded,
})

/*
 * Old local implementation of recently watched channels
 * leave it here for now, should be removed later, when all wbs instances are updated to 3.3
 */

export const addTvChannelLocal = createEvent<TvChannel>()

const $allLocalRecentlyWatched = createStore<{
  [id: string]: TvChannel[]
}>({})

// we do not use scopes for now, so, ignore this rule
// eslint-disable-next-line effector/require-pickup-in-persist
persist({
  store: $allLocalRecentlyWatched,
  dbName: 'tria__behavior',
  storeName: 'recent',
  key: 'channels',
  fail: noopEvent,
})

export const $recentlyWatchedTitle = combine(i18nModel.$t, ({ t }) =>
  t('live_channels_recently_watched')
)

export const $localRecentlyWatched = combine(
  $allLocalRecentlyWatched,
  session.$accountNumber,
  (all, key) => (key == null ? [] : (all[key] ?? []))
)

sample({
  clock: addTvChannelLocal,
  source: {
    all: $allLocalRecentlyWatched,
    key: session.$accountNumber,
    isLockable: pinLockFeature.$isLockableFn,
  },
  filter: ({ key, isLockable }, channel) =>
    key != null && nay(isLockable(channel)),
  fn: ({ all, key }, channel) =>
    produce(all, (draft) => {
      const k = key!
      const channels = (all[k] ?? []).slice(0)

      // remove channel if it already exists in list
      const idx = channels.findIndex(({ id }) => id === channel.id)
      if (idx > -1) channels.splice(idx, 1)

      // add channel to the beginning of the list
      channels.unshift(channel)
      draft[k] = channels.slice(0, RECENTLY_WATCHED_RAW_MAX_COUNT)
    }),
  target: $allLocalRecentlyWatched,
})

// update recently watched channels in storage in case of purchase
sample({
  clock: ssePurchasesModel.$channel,
  source: {
    all: $allLocalRecentlyWatched,
    key: session.$accountNumber,
  },
  filter: ({ all, key }) => key != null && key in all,
  fn: ({ all, key }, adjust) =>
    produce(all, (draft) => {
      const k = key!
      const channels = all[k] ?? []
      draft[k] = channels.map(adjust)
    }),
  target: $allLocalRecentlyWatched,
})

/*
 * Common
 */

export const updateRecentlyWatched = createEvent()

const $remoteRecentlyWatched = createStore<TvChannel[]>([]).reset(
  sessionModel.$session
)

// choose between local and remote recently watched channels
// depending on the presence of `appearanceCustomizationEnabled` flag
export const $recentlyWatchedFormat = combine(
  {
    features: features.$featuredFlags,
    local: $localRecentlyWatched,
    remote: $remoteRecentlyWatched,
    t: i18nModel.$t,
  },
  ({ features, local, remote }) =>
    (features.appearanceCustomizationEnabled == null ? local : remote).map(
      (channel) => extendMediaItemByLabelPriceTvChannel(channel)
    )
)

// trigger lazy update of remote recently watched channels, throttled by 1 minute
sample({
  clock: updateRecentlyWatched,
  source: {
    features: features.$featuredFlags,
    pending: api.tv.getRecentlyWatchedChannelsFx.pending,
    isGuest: guest.$guest,
  },
  filter: ({ features, pending, isGuest }) =>
    features.appearanceCustomizationEnabled != null && !pending && !isGuest,
  fn: noop,
  target: api.tv.getRecentlyWatchedChannelsFx,
})

// update remote recently watched channels
// what to do with paged requests?
sample({
  clock: api.tv.getRecentlyWatchedChannelsFx.doneData,
  fn: (data) => data.content,
  target: $remoteRecentlyWatched,
})

// handle direct api call to recently watched from home module via `api.appearance.getRowContentFx` effect
// what to do with paged requests?
sample({
  clock: api.appearance.getRowContentFx.done,
  filter: ({ params }) =>
    params.localizedTitleKey === 'live_channels_recently_watched',
  fn: ({ result }) => result.items,
  target: $remoteRecentlyWatched,
})
